// ** React Imports
import { createContext, useMemo, useState } from 'react'

// ** Config
import {
  fetchDashboardAudiencesUrl,
  fetchDashboardSegmentsUrl,
  fetchDashboardChannelVolumesUrl,
  fetchDashboardOpeningRateUrl,
  fetchDashboardCampaignsUrl,
  fetchDashboardClientEvent
} from 'src/configs/api'
import PropTypes from 'prop-types'
import { useAPI } from 'src/utils/api'

const defaultProvider = {
  msgSent: 0,
  setMsgSent: () => null,
  msgOpened: 0,
  setMsgOpened: () => null,
  audienceRange: 'total',
  setAudienceRange: () => null,
  eventsVolumnRange: 'total',
  setEventsVolumnRange: () => null,
  eventsOpeningRateRange: 'total',
  setEventsOpeningRateRange: () => null,
  audienceCount: 0,
  setAudienceCount: () => null,
  volumnCount: 0,
  setVolumnCount: () => null,
  topSegments: [],
  setTopSegments: () => null,
  scheduledCampaignList: [],
  setScheduledCampaignList: () => null,
  emailSent: 0,
  setEmailSent: () => null,
  whatsappSent: 0,
  setWhatsappSent: () => null,
  smsSent: 0,
  setSmsSent: () => null,
  ongoingCampaignCount: 0,
  setOngoingCampaignCount: () => null,
  holidaysList: [],
  setHolidaysList: () => null,
  announcementsList: [],
  setAnnouncementsList: () => null,
}


const DashboardContext = createContext(defaultProvider)

const DashboardProvider = ({ children }) => {
  const [audienceCount, setAudienceCount] = useState(defaultProvider.audienceCount)
  const [volumnCount, setVolumnCount] = useState(defaultProvider.volumnCount)
  const [topSegments, setTopSegments] = useState(defaultProvider.topSegments)
  const [emailSent, setEmailSent] = useState(defaultProvider.emailSent)
  const [whatsappSent, setWhatsappSent] = useState(defaultProvider.whatsappSent)
  const [smsSent, setSmsSent] = useState(defaultProvider.smsSent)
  const [audienceRange, setAudienceRange] = useState(defaultProvider.audienceRange)
  const [eventsVolumnRange, setEventsVolumnRange] = useState(defaultProvider.eventsVolumnRange)
  const [eventsOpeningRateRange, setEventsOpeningRateRange] = useState(defaultProvider.eventsOpeningRateRange)
  const [scheduledCampaignList, setScheduledCampaignList] = useState(defaultProvider.scheduledCampaignList)
  const [msgSent, setMsgSent] = useState(defaultProvider.msgSent)
  const [msgOpened, setMsgOpened] = useState(defaultProvider.msgOpened)
  const [ongoingCampaignCount, setOngoingCampaignCount] = useState(defaultProvider.ongoingCampaignCount)
  const [holidaysList, setHolidaysList] = useState(defaultProvider.holidaysList)
  const [announcementsList, setAnnouncementsList] = useState(defaultProvider.announcementsList)
  const api = useAPI()
  const fetchDashboardAudiencesData = async () => {
    try {
      //const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)
      const queryParams = {
        range: audienceRange
      }
      const response = await api.get(fetchDashboardAudiencesUrl, {
        // headers: {
        //   Authorization: storedToken
        // },
        params: queryParams
      })

      setAudienceCount(response?.data?.data?.audience_count || 0)
    } catch {

    }
  }
  const fetchDashboardChannelVolumnData = async () => {
    try {
      const queryParams = {
        range: eventsVolumnRange
      }
      const response = await api.get(fetchDashboardChannelVolumesUrl, {
        // headers: {
        //   Authorization: storedToken
        // },
        params: queryParams
      })
      setVolumnCount(response?.data?.data?.total_msg_sent || 0)
      setEmailSent(response?.data?.data?.email_sent || 0)
      setWhatsappSent(response?.data?.data?.whatsapp_sent || 0)
      setSmsSent(response?.data?.data?.sms_sent || 0)
    } catch {

    }
  }
  const fetchDashboardOpeningRateData = async () => {
    try {
      //const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)
      const queryParams = {
        range: eventsOpeningRateRange
      }
      const response = await api.get(fetchDashboardOpeningRateUrl, {
        // headers: {
        //   Authorization: storedToken
        // },
        params: queryParams
      })
      setMsgSent(response?.data?.data?.msg_sent || 0)
      setMsgOpened(response?.data?.data?.msg_opened || 0)
    } catch {


    }
  }

  const fetchDashboardHolidays = async () => {
    try {
      const response = await api.get(fetchDashboardClientEvent, {
        params: { eventType: 'holidays' }
      })
      const data = response?.data?.data || []

      if (data && data.length > 0) {
        // Format the data to display
        const formattedHolidays = data.map(holiday => {
          const date = new Date(holiday.date)
          const options = { day: '2-digit', month: 'short' }
          const formattedDate = date.toLocaleDateString('en-GB', options)
          const dayOptions = { weekday: 'long' }
          const dayName = date.toLocaleDateString('en-US', dayOptions)

          return {
            date: formattedDate,
            day: dayName,
            title: holiday.title,
            description: holiday.description
          }
        })
        setHolidaysList(formattedHolidays)
      } else {
        setHolidaysList([])
      }
    } catch {
      setHolidaysList([])
    }
  }

  const fetchDashboardAnnouncements = async () => {
    try {
      const response = await api.get(fetchDashboardClientEvent, {
        params: { eventType: 'announcements' }
      })
      const data = response?.data?.data || []

      if (data && data.length > 0) {
        // Format the data to display
        const formattedAnnouncements = data.map(announcement => {
          const date = new Date(announcement.date)
          const options = { day: '2-digit', month: 'short' }
          const formattedDate = date.toLocaleDateString('en-GB', options)

          return {
            date: formattedDate,
            title: announcement.title,
            description: announcement.description
          }
        })
        setAnnouncementsList(formattedAnnouncements)
      } else {
        setAnnouncementsList([])
      }
    } catch {
      setAnnouncementsList([])
    }
  }


  const fetchDashboardSegmentsData = async () => {
    try {
      const response = await api.get(
        fetchDashboardSegmentsUrl
     
      )
      setTopSegments(response?.data?.data?.top_segments || [])
    } catch {


    }
  }
  const fetchDashboardCampaignsData = async () => {
    try {
      const response = await api.get(
        fetchDashboardCampaignsUrl
       
      )
      const dataArray = response?.data?.data

      setOngoingCampaignCount(dataArray[dataArray?.length - 1] || 0)
      setScheduledCampaignList(dataArray?.slice(0, -1) || [])
    } catch {

      
    }
  }


  const values = useMemo(() => ({
    msgSent,
    setMsgSent,
    msgOpened,
    setMsgOpened,
    scheduledCampaignList,
    setScheduledCampaignList,
    audienceRange,
    setAudienceRange,
    eventsVolumnRange,
    setEventsVolumnRange,
    eventsOpeningRateRange,
    setEventsOpeningRateRange,
    audienceCount,
    setAudienceCount,
    volumnCount,
    setVolumnCount,
    topSegments,
    setTopSegments,
    emailSent,
    setEmailSent,
    whatsappSent,
    setWhatsappSent,
    smsSent,
    setSmsSent,
    ongoingCampaignCount,
    setOngoingCampaignCount,
    fetchDashboardAudiencesData: fetchDashboardAudiencesData,
    fetchDashboardSegmentsData: fetchDashboardSegmentsData,
    fetchDashboardChannelVolumnData: fetchDashboardChannelVolumnData,
    fetchDashboardOpeningRateData: fetchDashboardOpeningRateData,
    fetchDashboardCampaignsData: fetchDashboardCampaignsData,
    holidaysList,
    setHolidaysList,
    fetchDashboardHolidays: fetchDashboardHolidays,
    announcementsList,
    setAnnouncementsList,
    fetchDashboardAnnouncements: fetchDashboardAnnouncements
  }))
  return <DashboardContext.Provider value={values}>{children}</DashboardContext.Provider>
}

DashboardProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { DashboardContext, DashboardProvider, defaultProvider }
